import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { HeartPulse, RotateCcw } from 'lucide-react';

/**
 * Inline "Hypercare scope" panel. Appears in Step 4 when the Hypercare Support
 * component is selected. Drives the computed billable hours via
 * weeks × fte × hours_per_day × days_per_week.
 *
 * Props:
 *  - scope: null | { weeks, fte, hours_per_day, days_per_week }
 *  - onChange(scope): called with new scope object, or null to revert to preset
 *  - presetHours: number — the current simple/standard/complex preset (for "Use preset" fallback display)
 */
const DEFAULT_SCOPE = { weeks: 4, fte: 0.5, hours_per_day: 4, days_per_week: 5 };

export function HypercareScopePanel({ scope, onChange, presetHours }) {
  const active = scope || DEFAULT_SCOPE;
  const isCustom = scope !== null && scope !== undefined;
  const total = Math.round((active.weeks || 0) * (active.fte || 0) * (active.hours_per_day || 0) * (active.days_per_week || 0) * 10) / 10;

  const patch = (key, val) => {
    const next = { ...active, [key]: val };
    onChange(next);
  };

  const field = (key, label, min, max, step = 1) => (
    <div className="flex flex-col gap-1">
      <Label className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</Label>
      <Input
        type="number"
        value={active[key]}
        onChange={e => patch(key, parseFloat(e.target.value) || 0)}
        min={min} max={max} step={step}
        className="h-8 text-xs w-full"
        data-testid={`hypercare-${key}`}
      />
    </div>
  );

  return (
    <div className="mt-3 rounded-lg border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/60 p-3"
         data-testid="hypercare-scope-panel">
      <div className="flex items-center gap-2 mb-2">
        <HeartPulse className="w-4 h-4 text-orange-600" />
        <div className="text-xs font-semibold text-orange-900">Hypercare scope</div>
        <div className="ml-auto flex items-center gap-2">
          {isCustom && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}
              className="h-6 text-[10px] text-slate-600 hover:text-rose-600 px-2"
              data-testid="hypercare-reset-btn">
              <RotateCcw className="w-3 h-3 mr-1" />
              Use preset ({presetHours}h)
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {field('weeks', 'Weeks', 1, 26)}
        {field('fte', 'FTE', 0.1, 3, 0.1)}
        {field('hours_per_day', 'Hrs / day', 1, 12, 0.5)}
        {field('days_per_week', 'Days / week', 1, 7, 1)}
      </div>
      <div className="mt-2 text-[11px] text-slate-600 flex items-center justify-between"
           data-testid="hypercare-formula">
        <span className="font-mono">
          {active.weeks} × {active.fte} × {active.hours_per_day} × {active.days_per_week}
        </span>
        <span className="font-semibold text-orange-900">
          = {total.toLocaleString()} hrs
        </span>
      </div>
      {!isCustom && (
        <div className="mt-1 text-[10px] text-slate-500">
          Edit any value above to override the preset ({presetHours}h).
        </div>
      )}
    </div>
  );
}
